<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import PendenciaRelatorioAPI from 'dashboard/api/fandata/pendenciaRelatorio';
import SlaRelatorioAPI from 'dashboard/api/fandata/slaRelatorio';
import CortinaConversas from './components/CortinaConversas.vue';

// Relatório de pendência por parceiro (Bloco 2 / T-05): quanto cada parceiro segurou.
const { t } = useI18n();

const customDateRange = ref([subDays(new Date(), 29), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const sinceTs = ref(getUnixStartOfDay(customDateRange.value[0]));
const untilTs = ref(getUnixEndOfDay(customDateRange.value[1]));

const data = reactive({ parceiros: [], total: {}, assuntos: [] });

// T-259 (02/10): o seletor de parceiro, que filtra a PÁGINA INTEIRA. Pedido da Tamara: "eu
// seleciono o parceiro e relaciona todos os indicadores da página".
//
// A tabela "por parceiro" continua mostrando TODOS, de propósito: ela é a própria comparação
// entre eles, e filtrada por um só viraria uma linha, sem de onde escolher o próximo. O que o
// filtro muda são os números do topo e a leitura por causa raiz.
const parceiroFiltrado = ref('');
// T-122: o relatório acima diz quanto tempo cada parceiro segurou. Este responde outra
// pergunta, que é a da Tamara: entre os prazos que estouraram, quantos estavam com terceiro.
const sla = ref(null);

const fmtDur = seg => {
  if (!seg) return '—';
  if (seg < 60) return '<1min';
  if (seg < 3600) return `${Math.round(seg / 60)}min`;
  const h = Math.floor(seg / 3600);
  const m = Math.round((seg % 3600) / 60);
  return `${h}h${String(m).padStart(2, '0')}`;
};

// A lista do seletor sai do PRÓPRIO relatório, e não do cadastro de parceiros: só aparece
// quem teve pendência no período. Se o período muda e o escolhido sai da lista, ele continua
// como opção enquanto o filtro estiver ligado, senão o seletor ficaria em branco com filtro ativo.
const opcoesParceiro = computed(() => {
  const lista = (data.parceiros || []).map(p => ({
    slug: p.parceiro_slug,
    nome: p.parceiro_nome,
  }));
  const escolhido = parceiroFiltrado.value;
  if (escolhido && !lista.some(p => p.slug === escolhido)) {
    lista.unshift({ slug: escolhido, nome: escolhido });
  }
  return lista;
});

const nomeDoParceiroFiltrado = computed(
  () => opcoesParceiro.value.find(p => p.slug === parceiroFiltrado.value)?.nome || ''
);

// Com um parceiro escolhido, "Parceiros: 1" não informa nada. O card passa a dizer o que a
// pessoa quer saber ao filtrar: quanto daquele período é dele.
const participacao = computed(() => {
  const todas = (data.parceiros || []).reduce((acc, p) => acc + (p.pendencias || 0), 0);
  if (!todas) return '0%';
  return `${Math.round(((data.total.pendencias || 0) * 100) / todas)}%`;
});

const counters = computed(() => [
  parceiroFiltrado.value
    ? {
        key: 'PARTICIPACAO',
        value: participacao.value,
        accent: 'text-n-slate-12',
      }
    : {
        key: 'PARCEIROS',
        value: data.parceiros.length,
        accent: 'text-n-slate-12',
      },
  {
    key: 'PENDENCIAS',
    value: data.total.pendencias ?? 0,
    accent: 'text-n-slate-12',
  },
  {
    key: 'TEMPO_TOTAL',
    value: fmtDur(data.total.total_seg),
    accent: 'text-n-amber-11',
  },
  {
    key: 'TEMPO_MEDIO',
    value: fmtDur(data.total.media_seg),
    accent: 'text-n-slate-12',
  },
]);

const tipoLabel = tipo => t(`PENDENCIA_RELATORIO.TIPO.${tipo}`, tipo);

const fetchData = async () => {
  try {
    const { data: res } = await PendenciaRelatorioAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
      parceiro: parceiroFiltrado.value || undefined,
    });
    data.parceiros = res.parceiros || [];
    data.total = res.total || {};
    data.assuntos = res.assuntos || [];
  } catch (e) {
    useAlert(t('PENDENCIA_RELATORIO.ERROR'));
  }
  // Falha aqui não derruba o painel: o relatório de pendência é o principal, e o corte de
  // estouro por parceiro é leitura adicional.
  try {
    const { data: res } = await SlaRelatorioAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
    });
    sla.value = res;
  } catch (e) {
    sla.value = null;
  }
};

// T-259: "os blocos abrem a cortina lateral com as conversas". A lista sai do MESMO lugar de onde
// o número saiu (os ciclos encerrados no período), porque lista e número vindos de contas
// diferentes é como se descobre, meses depois, que a tela mente.
const cortina = reactive({
  aberta: false,
  titulo: '',
  subtitulo: '',
  carregando: false,
  conversas: [],
  total: null,
});

const fecharCortina = () => {
  cortina.aberta = false;
};

const abrirCortina = async ({ titulo, subtitulo, parceiro, assunto }) => {
  Object.assign(cortina, {
    aberta: true,
    titulo,
    subtitulo,
    carregando: true,
    conversas: [],
    total: null,
  });
  try {
    const { data: res } = await PendenciaRelatorioAPI.conversas({
      since: sinceTs.value,
      until: untilTs.value,
      parceiro,
      assunto,
    });
    cortina.conversas = res.conversas || [];
    cortina.total = res.total ?? null;
  } catch (e) {
    useAlert(t('PENDENCIA_RELATORIO.ERROR'));
    cortina.aberta = false;
  } finally {
    cortina.carregando = false;
  }
};

const abrirParceiro = row =>
  abrirCortina({
    titulo: row.parceiro_nome,
    subtitulo: t('PENDENCIA_RELATORIO.CORTINA.POR_PARCEIRO', {
      pendencias: row.pendencias,
      tempo: fmtDur(row.total_seg),
    }),
    parceiro: row.parceiro_slug,
  });

const abrirAssunto = linha =>
  abrirCortina({
    titulo: linha.label,
    subtitulo: t('PENDENCIA_RELATORIO.CORTINA.POR_ASSUNTO', {
      pendencias: linha.pendencias,
      tempo: fmtDur(linha.total_seg),
    }),
    assunto: linha.assunto,
    parceiro: parceiroFiltrado.value || undefined,
  });

const setParceiro = slug => {
  if (parceiroFiltrado.value === slug) return;
  parceiroFiltrado.value = slug;
  fetchData();
};

const onDateRangeChange = value => {
  const [start, end, rangeType] = value;
  customDateRange.value = [start, end];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  sinceTs.value = getUnixStartOfDay(start);
  untilTs.value = getUnixEndOfDay(end);
  fetchData();
};

// O estouro de SLA também responde ao filtro, senão a página teria um bloco falando da
// operação inteira debaixo de três que falam de um parceiro só.
const slaDoParceiro = computed(() => {
  if (!parceiroFiltrado.value || !sla.value) return null;
  return (
    sla.value.parceiros?.find(p => p.slug === parceiroFiltrado.value) || {
      slug: parceiroFiltrado.value,
      nome: nomeDoParceiroFiltrado.value,
      qtd: 0,
      pct: 0,
    }
  );
});

const { startRefetching } = useLiveRefresh(fetchData, 60000);
onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col w-full h-full px-6 py-4 overflow-auto">
    <!-- Header -->
    <div class="flex flex-col gap-3 mb-4">
      <div class="flex items-center gap-2">
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('PENDENCIA_RELATORIO.HEADER') }}
        </h1>
      </div>
      <p class="text-sm text-n-slate-11 -mt-1">
        {{ t('PENDENCIA_RELATORIO.HINT') }}
      </p>
      <p
        v-if="parceiroFiltrado"
        class="flex items-center gap-1.5 -mt-1 text-sm text-n-amber-11"
      >
        <span class="i-lucide-filter size-4 shrink-0" />
        {{
          t('PENDENCIA_RELATORIO.FILTRO_ATIVO', {
            parceiro: nomeDoParceiroFiltrado,
          })
        }}
      </p>
      <div class="flex items-start flex-wrap gap-3">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
        <div v-if="opcoesParceiro.length" class="flex items-center gap-2">
          <span
            class="text-xs font-semibold tracking-wide uppercase text-n-slate-11"
          >
            {{ t('PENDENCIA_RELATORIO.PARCEIRO') }}
          </span>
          <select
            :value="parceiroFiltrado"
            class="px-2 py-1 text-sm border rounded-md bg-n-alpha-1 border-n-weak text-n-slate-12"
            @change="setParceiro($event.target.value)"
          >
            <option value="">
              {{ t('PENDENCIA_RELATORIO.PARCEIRO_TODOS') }}
            </option>
            <option v-for="p in opcoesParceiro" :key="p.slug" :value="p.slug">
              {{ p.nome }}
            </option>
          </select>
        </div>
        <BaixarRelatorio
          nome="pendencia"
          :dados="data"
          :since="sinceTs"
          :until="untilTs"
        />
      <!-- T-259 (02/10): o atalho saiu por pedido da Tamara, pelo mesmo motivo da Satisfação:
         ele direciona para a aba Tempo de atendimento. -->
      </div>
    </div>

    <!-- Contadores -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      <div
        v-for="c in counters"
        :key="c.key"
        class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <span class="text-xs font-medium text-n-slate-11">
          {{ t(`PENDENCIA_RELATORIO.COUNTERS.${c.key}`) }}
        </span>
        <span class="text-2xl font-semibold" :class="c.accent">{{
          c.value
        }}</span>
      </div>
    </div>

    <!-- Por parceiro -->
    <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
      <div class="px-5 py-3 border-b border-n-weak">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('PENDENCIA_RELATORIO.BY_PARTNER') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('PENDENCIA_RELATORIO.BY_PARTNER_HINT') }}
          {{ t('PENDENCIA_RELATORIO.CORTINA.DICA') }}
          <template v-if="parceiroFiltrado">
            {{ t('PENDENCIA_RELATORIO.BY_PARTNER_COMPARA') }}
          </template>
        </p>
      </div>

      <div v-if="!data.parceiros.length" class="px-5 py-10 text-center">
        <p class="text-sm text-n-slate-11">
          {{ t('PENDENCIA_RELATORIO.EMPTY') }}
        </p>
      </div>

      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-n-slate-11">
            <th class="text-left font-medium px-5 py-2">
              {{ t('PENDENCIA_RELATORIO.PARCEIRO') }}
            </th>
            <th class="font-medium px-3 py-2 text-center">
              {{ t('PENDENCIA_RELATORIO.COL_TIPO') }}
            </th>
            <th class="font-medium px-3 py-2 text-center">
              {{ t('PENDENCIA_RELATORIO.COL_QTD') }}
            </th>
            <th class="font-medium px-3 py-2 text-center">
              {{ t('PENDENCIA_RELATORIO.COL_TOTAL') }}
            </th>
            <th class="font-medium px-3 py-2 text-center">
              {{ t('PENDENCIA_RELATORIO.COL_MEDIA') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in data.parceiros"
            :key="row.parceiro_slug"
            class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
            :class="
              row.parceiro_slug === parceiroFiltrado
                ? 'bg-n-amber-2 font-medium'
                : ''
            "
            @click="abrirParceiro(row)"
          >
            <td class="px-5 py-2 font-medium text-n-slate-12 capitalize">
              {{ row.parceiro_nome }}
            </td>
            <td class="px-3 py-2 text-center text-n-slate-11 capitalize">
              {{ tipoLabel(row.tipo) }}
            </td>
            <td class="px-3 py-2 text-center text-n-slate-12">
              {{ row.pendencias }}
            </td>
            <td class="px-3 py-2 text-center font-semibold text-n-amber-11">
              {{ fmtDur(row.total_seg) }}
            </td>
            <td class="px-3 py-2 text-center text-n-slate-12">
              {{ fmtDur(row.media_seg) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- T-150i: por parceiro diz QUEM segurou; por causa raiz diz O QUÊ ficou preso. Saber que a
         FENG segurou 40 horas não diz o que fazer; saber que 30 delas eram cancelamento diz. -->
    <div v-if="data.assuntos.length" class="flex flex-col gap-2 mt-8">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('PENDENCIA_RELATORIO.ASSUNTOS.TITULO') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('PENDENCIA_RELATORIO.ASSUNTOS.HINT') }}
      </p>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden mt-1">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-n-slate-11">
              <th class="text-left font-medium px-5 py-2">
                {{ t('PENDENCIA_RELATORIO.ASSUNTOS.COL_ASSUNTO') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('PENDENCIA_RELATORIO.ASSUNTOS.COL_QTD') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('PENDENCIA_RELATORIO.ASSUNTOS.COL_TOTAL') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('PENDENCIA_RELATORIO.ASSUNTOS.COL_MEDIA') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="linha in data.assuntos"
              :key="linha.assunto"
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
              @click="abrirAssunto(linha)"
            >
              <td class="px-5 py-2 font-medium text-n-slate-12 capitalize">
                {{ linha.label }}
              </td>
              <td class="px-3 py-2 text-center">{{ linha.pendencias }}</td>
              <td class="px-3 py-2 text-center">{{ fmtDur(linha.total_seg) }}</td>
              <td class="px-3 py-2 text-center">{{ fmtDur(linha.media_seg) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- T-122: entre os prazos que estouraram, quantos estavam com terceiro. É a resposta para
         "80% dos meus chamados estouraram, 35% é na FENG": a tabela acima mostra tempo por
         parceiro, esta mostra responsabilidade pelo atraso. -->
    <div v-if="sla && sla.estouradas > 0" class="flex flex-col gap-2 mt-8">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('PENDENCIA_RELATORIO.SLA.TITULO') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        <template v-if="slaDoParceiro">
          {{
            t('PENDENCIA_RELATORIO.SLA.RESUMO_PARCEIRO', {
              qtd: slaDoParceiro.qtd,
              estouradas: sla.estouradas,
              parceiro: slaDoParceiro.nome,
              pct: slaDoParceiro.pct,
            })
          }}
        </template>
        <template v-else>
          {{
            t('PENDENCIA_RELATORIO.SLA.RESUMO', {
              estouradas: sla.estouradas,
              avaliadas: sla.avaliadas,
              taxa: sla.taxa_estouro,
            })
          }}
        </template>
      </p>
      <div v-if="!slaDoParceiro" class="flex flex-wrap gap-2 mt-1">
        <span
          v-for="p in sla.parceiros"
          :key="p.slug"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
        >
          <span class="font-semibold capitalize">{{ p.nome }}</span>
          <span>{{ p.qtd }}</span>
          <span class="text-n-amber-10">({{ p.pct }}%)</span>
        </span>
        <span
          v-if="sla.sem_parceiro > 0"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-n-alpha-2 text-n-slate-11"
        >
          <span class="font-semibold">{{ t('PENDENCIA_RELATORIO.SLA.SEM_PARCEIRO') }}</span>
          <span>{{ sla.sem_parceiro }}</span>
        </span>
      </div>
    </div>

    <CortinaConversas
      v-if="cortina.aberta"
      :titulo="cortina.titulo"
      :subtitulo="cortina.subtitulo"
      :conversas="cortina.conversas"
      :carregando="cortina.carregando"
      :total="cortina.total"
      @fechar="fecharCortina"
    >
      <template #direita="{ linha }">
        <span class="text-xs tabular-nums text-n-amber-11 whitespace-nowrap">
          {{ fmtDur(linha.total_seg) }}
          <template v-if="linha.pendencias > 1">
            · {{ linha.pendencias }}x
          </template>
        </span>
      </template>
    </CortinaConversas>
  </div>
</template>
